import type { ConnectResult, ThrowSession } from "@airspace/shared";

/**
 * Typisierter Zugriff auf die Airspace-API.
 *
 * Frontend und API liegen auf demselben Origin, deshalb genuegen relative
 * Pfade und es braucht weder CORS-Konfiguration noch eine Basis-URL.
 *
 * Der Browser setzt bei jeder Anfrage, die nicht GET oder HEAD ist,
 * automatisch den Origin-Header. Genau darauf stuetzt sich die serverseitige
 * Origin-Pruefung -- hier ist dafuer nichts zu tun.
 */

/** Fehler mit dem maschinenlesbaren Code aus der API-Antwort. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = "ApiRequestError";
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: body === undefined ? {} : { "content-type": "application/json" },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    // Netzwerkfehler: der Server war nicht erreichbar. Als eigener Code, damit
    // die Oberflaeche "keine Verbindung" von "abgewiesen" unterscheiden kann.
    throw new ApiRequestError(0, "network_error", "Keine Verbindung zum Server.", { cause });
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? "unknown_error",
      error?.message ?? "Unerwarteter Fehler.",
    );
  }

  return payload as T;
}

export interface IncomingRequest {
  requestId: string;
  fromName: string;
  createdAt: string;
  expiresAt: string;
}

export interface SessionStateResponse {
  session: ThrowSession;
  incomingRequests: IncomingRequest[];
}

/** Ergebnis eines Verbindungsversuchs: Anfrage gestellt oder Vault-Einstieg. */
export type ConnectResponse =
  | ({ kind: "request" } & ConnectResult)
  /** `redirectUrl` fehlt, wenn Authentik nicht eingerichtet ist. */
  | { kind: "vault"; available: boolean; redirectUrl?: string };

export const api = {
  /** Laedt den vollstaendigen Sitzungszustand. Legt beim ersten Mal alles an. */
  getSession: () => request<SessionStateResponse>("GET", "/api/throw/session"),

  renewPin: () => request<{ pin: string; expiresAt: string }>("POST", "/api/throw/pin/renew"),

  connect: (pin: string) => request<ConnectResponse>("POST", "/api/throw/connect", { pin }),

  acceptRequest: (requestId: string) =>
    request<{ pairingId: string; peerName: string; expiresAt: string }>(
      "POST",
      `/api/throw/requests/${encodeURIComponent(requestId)}/accept`,
    ),

  rejectRequest: (requestId: string) =>
    request<void>("POST", `/api/throw/requests/${encodeURIComponent(requestId)}/reject`),

  leavePairing: () => request<void>("POST", "/api/throw/pairing/leave"),
};
